import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, HeartPulse, ShieldCheck, ArrowLeft, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function MaSante() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/health/me`)
      .then((r) => setForm(r.data))
      .catch(() => toast.error('Impossible de charger votre fiche santé.'));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.consent) { toast.error("Merci d'autoriser le partage de ces informations avec l'équipe PURO."); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/health/me`, {
        contraindications: form.contraindications || '',
        pregnancy: !!form.pregnancy,
        pregnancy_detail: form.pregnancy_detail || '',
        medical_history: form.medical_history || '',
        consent: !!form.consent,
      });
      setForm(r.data);
      toast.success('Fiche santé enregistrée. Votre équipe PURO en est informée.');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement votre fiche santé ? Vos coachs n\'y auront plus accès.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/health/me`);
      setForm({ contraindications: '', pregnancy: false, pregnancy_detail: '', medical_history: '', consent: false, has_alert: false, updated_at: null });
      toast.success('Votre fiche santé a été supprimée.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression.');
    } finally { setSaving(false); }
  };

  if (!form) {
    return <div className="min-h-screen bg-[#F5EFE5] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C99A55]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F5EFE5]" data-testid="ma-sante-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/profil" className="inline-flex items-center gap-1.5 text-sm text-[#7A6455] hover:text-[#493329] mb-5" data-testid="back-to-profil">
          <ArrowLeft className="w-4 h-4" /> Mon profil
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-[#C99A55]/15 flex items-center justify-center">
            <HeartPulse className="w-5 h-5 text-[#C99A55]" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="font-heading font-light text-2xl text-[#493329]">Ma fiche santé</h1>
            <p className="text-sm text-[#7A6455]">Pour pratiquer en toute sécurité</p>
          </div>
        </div>

        <div className="bg-[#EDE6D8] border border-[#E0D4BF] rounded-2xl p-4 mb-6 flex gap-3" data-testid="privacy-note">
          <Lock className="w-4 h-4 text-[#8B6914] mt-0.5 flex-shrink-0" strokeWidth={1.6} />
          <p className="text-xs text-[#6B5A3E] leading-relaxed">
            Ces informations sont strictement confidentielles. Elles ne sont visibles que par vos coachs PURO et l'administration du studio, uniquement pour adapter vos séances. Vous pouvez les modifier à tout moment.
          </p>
        </div>

        {form.has_alert && (
          <div className="bg-[#F7E6E1] border border-[#E4B9AC] rounded-2xl p-3 mb-6 text-sm text-[#8A4A3B]" data-testid="health-alert-badge">
            Votre fiche signale un point de vigilance — votre coach en tiendra compte en séance.
          </div>
        )}

        <Card className="bg-white border-[#E9DDCC] rounded-3xl mb-5">
          <CardContent className="p-6 space-y-5">
            <div>
              <label className="text-sm font-medium text-[#493329]">Blessures, contre-indications & zones sensibles</label>
              <p className="text-xs text-[#9A8B78] mb-2">Dos, genoux, épaules, opérations récentes, douleurs à surveiller…</p>
              <Textarea value={form.contraindications || ''} onChange={(e) => set('contraindications', e.target.value)}
                rows={3} placeholder="Ex. : hernie discale L5, prudence sur les flexions du dos." className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl" data-testid="health-contraindications" />
            </div>

            <div className="border-t border-[#F0E8DA] pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-[#493329]">Grossesse / post-partum</label>
                  <p className="text-xs text-[#9A8B78]">En cours de grossesse ou en période post-partum</p>
                </div>
                <Switch checked={!!form.pregnancy} onCheckedChange={(v) => set('pregnancy', v)} data-testid="health-pregnancy-switch" />
              </div>
              {form.pregnancy && (
                <Textarea value={form.pregnancy_detail || ''} onChange={(e) => set('pregnancy_detail', e.target.value)}
                  rows={2} placeholder="Trimestre, semaines depuis l'accouchement, recommandations médicales…" className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl mt-3" data-testid="health-pregnancy-detail" />
              )}
            </div>

            <div className="border-t border-[#F0E8DA] pt-5">
              <label className="text-sm font-medium text-[#493329]">Antécédents médicaux</label>
              <p className="text-xs text-[#9A8B78] mb-2">Pathologies, traitements en cours, allergies utiles à connaître.</p>
              <Textarea value={form.medical_history || ''} onChange={(e) => set('medical_history', e.target.value)}
                rows={3} placeholder="Ex. : asthme léger, hypertension suivie." className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl" data-testid="health-medical-history" />
            </div>
          </CardContent>
        </Card>

        <label className="flex items-start gap-3 bg-white border border-[#E9DDCC] rounded-2xl p-4 mb-5 cursor-pointer" data-testid="health-consent-row">
          <Checkbox checked={!!form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-0.5" data-testid="health-consent-checkbox" />
          <span className="text-xs text-[#6B5A3E] leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1 text-[#5C7057]" />
            J'autorise l'équipe PURO (coachs et administration) à consulter ces informations afin d'adapter mes séances en toute sécurité.
          </span>
        </label>

        <Button onClick={save} disabled={saving || !form.consent}
          className="w-full bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-xl h-12 btn-premium" data-testid="save-health-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer ma fiche santé'}
        </Button>
        {form.updated_at && (
          <>
            <p className="text-center text-xs text-[#a08f7e] mt-4" data-testid="health-updated-at">
              Dernière mise à jour : {new Date(form.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <button onClick={remove} disabled={saving} className="w-full text-center text-xs text-[#9A8B78] hover:text-[#C76542] mt-3 underline underline-offset-2" data-testid="delete-health-btn">
              Supprimer ma fiche santé
            </button>
          </>
        )}
      </div>
    </div>
  );
}
